/**
 * 意向规则配置页面
 *
 * 本页面用于管理客户意向等级的关键词匹配规则（IntentRule）。
 * 系统在 NLP 分析客户跟进记录时，会根据关键词命中情况判断客户的意向等级。
 *
 * 意向等级从 0 到 5 共 6 级：
 *   0 - 无法触达（空号、停机、关机等）
 *   1 - 无意向（明确拒绝、不需要等）
 *   2 - 待跟进（在忙、改天联系等）
 *   3 - 有意向（核资、邀约、加微信等）
 *   4 - 高意向（面签、确定时间等）
 *   5 - 成交中（已签约、已放款等）
 *
 * 页面以 Tabs 形式展示各意向等级，每个 Tab 下通过 TagInput 编辑该等级对应的关键词列表。
 * 所有关键词修改后一次性提交（保存全部），也可恢复为系统默认规则。
 */
import React, { useEffect, useState } from "react";
import { Card, Tabs, TagInput, Button, Toast, Spin, Space, Popconfirm, Descriptions } from "@douyinfe/semi-ui";
import { ApiClient } from "../../api/ApiClient";

const apiClient = ApiClient.getDefault();

/**
 * 意向规则数据接口
 * 对应后端 IntentRule 实体，描述一条意向等级的关键词规则
 */
interface IntentRule {
  /** 规则ID，新建时为空 */
  id?: number;
  /** 意向等级（0-5），对应 LEVEL_CONFIG 中的 level */
  intentLevel: number;
  /** 意向等级中文标签，如 "无法触达"、"有意向" 等 */
  intentLabel: string;
  /** 匹配关键词数组，客户沟通记录中出现这些关键词则判定为对应意向等级 */
  matchKeywords: string[];
  /** 排序序号 */
  sortOrder: number;
}

/**
 * 意向等级配置常量
 * 定义了 6 个意向等级的元数据：等级编号、中文标签、展示颜色、描述说明。
 * - color 用于 Tab 标签上的圆点颜色指示和 Tag 组件的颜色
 * - desc 作为说明文字展示在每个 Tab 面板顶部，帮助用户理解该等级的含义
 */
const LEVEL_CONFIG = [
  { level: 0, label: "无法触达", color: "grey", desc: "空号、停机、关机、拦截、未接等" },
  { level: 1, label: "无意向", color: "red", desc: "明确拒绝、不需要、挂机等" },
  { level: 2, label: "待跟进", color: "orange", desc: "在忙、外地、没时间、改天联系等" },
  { level: 3, label: "有意向", color: "blue", desc: "核资、邀约、加微信、需要等" },
  { level: 4, label: "高意向", color: "green", desc: "面签、明天过来、确定时间等" },
  { level: 5, label: "成交中", color: "purple", desc: "已签约、已放款、审批中等" },
];

export default function IntentRuleConfigPage() {
  /** 各意向等级对应的关键词列表，key 为等级编号，value 为关键词数组 */
  const [rules, setRules] = useState<Map<number, string[]>>(new Map());
  /** 各意向等级对应的后端规则 ID，用于区分新增和更新操作 */
  const [ruleIds, setRuleIds] = useState<Map<number, number>>(new Map());
  /** 页面加载状态 */
  const [loading, setLoading] = useState(false);
  /** 保存请求进行中状态 */
  const [saving, setSaving] = useState(false);
  /** 当前激活的 Tab 面板对应的意向等级（字符串形式的等级编号） */
  const [activeTab, setActiveTab] = useState("0");

  /** 组件挂载时加载意向规则数据 */
  useEffect(() => {
    loadData();
  }, []);

  /**
   * 加载意向规则列表
   * 调用 GET /intentRule/list 获取所有等级的关键词规则，
   * 将后端返回的数据解析为两个 Map：
   *   - rules: 等级 → 关键词数组的映射
   *   - ruleIds: 等级 → 规则ID的映射（用于后续保存时判断是新增还是更新）
   */
  const loadData = async () => {
    setLoading(true);
    try {
      const resp = await apiClient.request<any[]>("/intentRule/list", "GET");
      if (resp.data) {
        const map = new Map<number, string[]>();
        const ids = new Map<number, number>();
        for (const rule of resp.data) {
          // matchKeywords 在后端可能以 JSON 字符串或数组形式返回，需要兼容处理
          let keywords: string[] = [];
          if (typeof rule.matchKeywords === "string") {
            try {
              keywords = JSON.parse(rule.matchKeywords);
            } catch {
              keywords = [];
            }
          } else if (Array.isArray(rule.matchKeywords)) {
            keywords = rule.matchKeywords;
          }
          map.set(rule.intentLevel, keywords);
          if (rule.id) ids.set(rule.intentLevel, rule.id);
        }
        setRules(map);
        setRuleIds(ids);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 保存全部意向规则
   * 遍历所有 6 个意向等级，对每个等级：
   *   1. 从 rules Map 中获取当前关键词列表
   *   2. 检查 ruleIds Map 中是否已有后端 ID
   *   3. 有 ID 则调用更新接口，无 ID 则调用新增接口
   * 所有等级逐一保存完成后提示成功并刷新数据
   */
  const handleSave = async () => {
    setSaving(true);
    try {
      for (const config of LEVEL_CONFIG) {
        const keywords = rules.get(config.level) || [];
        const existingId = ruleIds.get(config.level);
        const body = {
          intentLevel: config.level,
          intentLabel: config.label,
          matchKeywords: JSON.stringify(keywords),
          sortOrder: config.level * 10,
        };

        if (existingId) {
          await apiClient.request("/intentRule/update", "POST", undefined, {
            id: existingId,
            ...body,
          });
        } else {
          await apiClient.request("/intentRule/save", "POST", undefined, body);
        }
      }
      Toast.success("保存成功");
      loadData();
    } catch (e: any) {
      Toast.error(e?.message || "保存失败");
    } finally {
      setSaving(false);
    }
  };

  /**
   * 恢复默认意向规则
   * 调用 POST /intentRule/resetDefaults 将所有等级的关键词重置为系统预设值
   */
  const handleReset = async () => {
    try {
      await apiClient.request("/intentRule/resetDefaults", "POST");
      Toast.success("已恢复默认规则");
      loadData();
    } catch (e: any) {
      Toast.error(e?.message || "操作失败");
    }
  };

  /**
   * 更新指定意向等级的关键词列表
   * 通过创建新的 Map 触发 React 状态更新
   */
  const updateKeywords = (level: number, keywords: string[]) => {
    setRules(new Map(rules.set(level, keywords)));
  };

  return (
    <div style={{ padding: 16, maxWidth: 800 }}>
      {/* 页面卡片：顶部包含恢复默认和保存全部按钮 */}
      <Card
        title="意向规则配置"
        headerExtraContent={
          <Space>
            <Popconfirm title="确定恢复默认规则？" onConfirm={handleReset}>
              <Button size="small">恢复默认</Button>
            </Popconfirm>
            <Button theme="solid" size="small" loading={saving} onClick={handleSave}>
              保存全部
            </Button>
          </Space>
        }
      >
        <Spin spinning={loading}>
          {/* 意向等级 Tabs：每个 Tab 对应一个意向等级 */}
          <Tabs activeKey={activeTab} onChange={(k) => setActiveTab(k as string)}>
            {LEVEL_CONFIG.map((config) => (
              <Tabs.TabPane
                itemKey={String(config.level)}
                /* Tab 标签：带颜色圆点指示 + 等级名称 + 等级编号 */
                tab={
                  <span>
                    <span
                      style={{
                        display: "inline-block",
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: config.color === "grey" ? "#999" : config.color,
                        marginRight: 6,
                      }}
                    />
                    {config.label}({config.level})
                  </span>
                }
              >
                {/* 等级说明：帮助用户理解该等级的判定标准 */}
                <Descriptions
                  data={[{ key: "说明", value: config.desc }]}
                  style={{ marginBottom: 12 }}
                />
                {/* 关键词输入组件：使用 TagInput 支持回车添加、失焦添加，最多 50 个关键词 */}
                <TagInput
                  value={rules.get(config.level) || []}
                  onChange={(vals: string[]) => updateKeywords(config.level, vals as string[])}
                  placeholder="输入关键词后回车添加"
                  style={{ width: "100%" }}
                  addOnBlur
                  maxLength={50}
                />
                {/* 关键词计数显示 */}
                <div style={{ marginTop: 8, color: "#999", fontSize: 12 }}>
                  当前 {rules.get(config.level)?.length || 0} 个关键词
                </div>
              </Tabs.TabPane>
            ))}
          </Tabs>
        </Spin>
      </Card>
    </div>
  );
}
